/**
 * Public /about page — personal broker story. Trust builder for buyers.
 *
 * Content is intentionally placeholder-flavored ("edit me") but written in
 * an authentic voice so it can go live as-is until the user swaps in a
 * personal draft. Pulls brand + contact info from settings/branding so the
 * heading and email always match the deployed brand.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Mail } from "lucide-react";
import { fetchBranding, api } from "@/lib/api";
import SiteHeader from "@/components/marketplace/SiteHeader";
import SiteFooter from "@/components/marketplace/SiteFooter";
import Seo from "@/components/Seo";
import CompareTray from "@/components/marketplace/CompareTray";
import SnitcherEmbed from "@/components/marketplace/SnitcherEmbed";
import BullseyeEmbed from "@/components/marketplace/BullseyeEmbed";

export default function AboutPage() {
  const [brand, setBrand] = useState({ brand_name: "CasperDomains" });
  const [broker, setBroker] = useState({
    firstName: "Casper",
    lastName: "",
    company: "CasperDomains",
    email: "hello@casperdomains.com",
  });

  useEffect(() => {
    fetchBranding().then(setBrand).catch(() => {});
    // Public settings-lite endpoint is auth-protected; try but don't error.
    api.get("/settings").then((r) => {
      const s = r.data || {};
      const read = (k) => (typeof s[k] === "string" ? s[k] : s[k]?.value) || "";
      setBroker((b) => ({
        ...b,
        firstName: read("CONTACT_FIRST_NAME") || b.firstName,
        lastName:  read("CONTACT_LAST_NAME")  || b.lastName,
        company:   read("CONTACT_COMPANY")    || b.company,
        email:     read("CONTACT_EMAIL")      || b.email,
      }));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    document.title = `About · ${brand.brand_name}`;
  }, [brand.brand_name]);

  const fullName = [broker.firstName, broker.lastName].filter(Boolean).join(" ") || "The broker";
  const customStory = (brand.about_story_md || "").trim();

  return (
    <div className="cd-marketplace" style={{ background: "var(--cd-white)", color: "var(--cd-black)", fontFamily: "var(--cd-font)" }} data-testid="about-page">
      <Seo
        title={`About · ${brand.brand_name}`}
        description={`The broker behind ${brand.brand_name}, a personal domain-acquisition service run by one person, not a marketplace.`}
        type="profile"
        siteName={brand.brand_name}
        image={brand.avatar_url || brand.logo_url}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Person",
          name: fullName,
          jobTitle: "Domain broker",
          worksFor: { "@type": "Organization", name: brand.brand_name },
          image: brand.avatar_url,
        }}
      />
      <SiteHeader />

      {/* Navy hero — restrained, no orbital art, just the eyebrow + headline */}
      <section className="cd-domain-hero" style={{ padding: "68px 0 88px" }}>
        <div className="cd-container" style={{ position: "relative", zIndex: 1 }}>
          <span className="cd-domain-tag">
            One broker · one desk · one thread
          </span>
          <h1
            className="cd-display"
            style={{ marginTop: 22, color: "#fff", maxWidth: 900 }}
            data-testid="about-headline"
          >
            The person on the other end of the email.
          </h1>
          {brand.avatar_url && (
            <img
              src={brand.avatar_url}
              alt={fullName}
              data-testid="about-avatar"
              style={{
                marginTop: 32,
                width: 84, height: 84, borderRadius: "50%",
                objectFit: "cover", border: "3px solid rgba(255,255,255,0.2)",
              }}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          )}
        </div>
      </section>

      {/* Body — long-form editorial */}
      <article
        className="cd-container"
        style={{ maxWidth: 780, padding: "72px 24px 96px", fontFamily: "var(--cd-font)" }}
        data-testid="about-body"
      >
        {customStory ? (
          <CustomStory
            markdown={customStory}
            brand={brand.brand_name}
            fullName={fullName}
            email={broker.email}
          />
        ) : (
          <DefaultStory brand={brand.brand_name} fullName={fullName} email={broker.email} />
        )}

        <div style={{ marginTop: 48, paddingTop: 32, borderTop: "1px solid var(--cd-gray-200)", display: "flex", flexWrap: "wrap", gap: 16, justifyContent: "space-between", alignItems: "center" }}>
          <a
            href={`mailto:${broker.email}`}
            data-testid="about-cta"
            className="cd-btn cd-btn--primary"
          >
            <Mail size={16} strokeWidth={1.8} />
            Say hello
          </a>
          <Link to="/#featured" className="cd-link" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            Browse the marketplace <ArrowUpRight size={14} />
          </Link>
        </div>
      </article>

      <SiteFooter />
      <CompareTray />
      <SnitcherEmbed />
      <BullseyeEmbed page="about" />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section style={{ marginTop: 40 }}>
      <h2 className="cd-eyebrow" style={{ marginBottom: 14 }}>
        {title}
      </h2>
      <div style={{ fontSize: 17, lineHeight: 1.7, color: "var(--cd-gray-700)", fontFamily: "var(--cd-font)" }}>
        {children}
      </div>
    </section>
  );
}

function DefaultStory({ brand, fullName, email }) {
  return (
    <>
      <p
        style={{ fontSize: 24, lineHeight: 1.55, color: "var(--cd-navy-950)", fontWeight: 500, letterSpacing: "-0.01em" }}
      >
        {brand} is not a marketplace. It's <strong style={{ fontWeight: 800 }}>{fullName}</strong>,
        working from a small studio outside Amsterdam, buying the domains
        that other people forgot to renew and matching them to the founders
        who need them.
      </p>

      <Section title="How it started">
        I bought my first expired domain in 2018, a three-letter <code style={{ background: "var(--cd-gray-100)", padding: "1px 6px", borderRadius: 4, fontSize: 14 }}>.io</code> that
        a Silicon Valley startup had let lapse by a single day. That mistake
        taught me two things. First: half the internet's best names are, at
        any moment, one week away from becoming available again. Second:
        catching them requires machinery, sub-second scheduling, multiple
        registrars, retries, that no casual buyer wants to build.
        <br /><br />
        So I built it. What was once a side-hobby to help friends secure
        their startup names became a full-time desk. Today the engine runs
        across four registrars, monitors thousands of expiring domains at any
        moment, and closes with buyers on four continents.
      </Section>

      <Section title="What you get">
        A personal broker, not a support ticket. When you email
        <a
          href={`mailto:${email}`}
          style={{ color: "var(--cd-blue-500)", fontWeight: 600, margin: "0 4px" }}
          data-testid="about-email"
        >{email}</a>
        you get me. Usually within one business day, occasionally within one
        hour. No form autoresponders, no chatbots, no marketplace fees
        stacked on top of the price.
        <br /><br />
        Every listing here I own outright. If the price on a lander feels
        steep, tell me the number you had in mind. Half the deals close
        under ask.
      </Section>

      <Section title="How I decide what to buy">
        Two heuristics. <em>Brandability</em>, a name a startup could put on
        a t-shirt and still look serious ten years later. And <em>keyword
        value</em>, an exact-match phrase somebody is already Googling.
        Domains that score high on both are rare, and those are the ones I
        keep in inventory instead of flipping fast.
        <br /><br />
        Everything else (hyphens, numbers, plurals, misspellings) I skip.
        The catalog you see is small on purpose.
      </Section>

      <Section title="Terms in one paragraph">
        Every domain is owned in my personal name (or the entity of the
        buyer's choice for enterprise deals) and pushed within 24 hours of
        confirmed payment. Payments run through Stripe (card / Apple Pay),
        Bunq iDEAL, direct SEPA, or Escrow.com on any deal over €5,000. Full
        refund if I can't complete the transfer for any reason on my side.
        Nothing to sign, no NDA, no lawyers unless you want them.
      </Section>
    </>
  );
}

/**
 * Ultra-tiny markdown renderer for the About page.
 * Supported grammar (deliberately minimal — the admin is a technical user):
 *   ## Section title          → renders as titled Section block
 *   (anything before first ##) → renders as italic lede paragraph
 *   blank line                → paragraph break
 *   **bold**   _italic_   [label](https://url)   `code`
 * Anything else is passed through as plain text (React auto-escapes it —
 * no HTML injection possible).
 */
function CustomStory({ markdown }) {
  const src = markdown.replace(/\r\n?/g, "\n").trim();
  // Split on section headers. Result is [ledePart, title1, body1, title2, body2, ...]
  const parts = src.split(/^##\s+(.+)$/m);
  const lede = (parts.shift() || "").trim();
  const sections = [];
  for (let i = 0; i < parts.length; i += 2) {
    sections.push({ title: (parts[i] || "").trim(), body: (parts[i + 1] || "").trim() });
  }

  return (
    <>
      {lede && (
        <div
          style={{ fontSize: 24, lineHeight: 1.55, color: "var(--cd-navy-950)", fontWeight: 500, letterSpacing: "-0.01em" }}
          data-testid="about-lede"
        >
          {renderParagraphs(lede)}
        </div>
      )}
      {sections.map((s, i) => (
        <Section key={i} title={s.title}>
          {renderParagraphs(s.body)}
        </Section>
      ))}
    </>
  );
}

/** Split body into <p>s on blank lines, single \n → <br/>. */
function renderParagraphs(text) {
  const paras = text.split(/\n{2,}/).filter((p) => p.trim() !== "");
  return paras.map((p, i) => (
    <p key={i} className={i > 0 ? "mt-5" : ""}>
      {renderInline(p)}
    </p>
  ));
}

/** Inline markdown → React nodes. Order matters: links first, then bold, then italic, then code. */
function renderInline(text) {
  // Split into segments preserving markers. We do sequential passes on an
  // array of (string | node) elements, only splitting the string entries.
  let segs = [text];

  const passes = [
    // [label](url) — links (allow http, https, mailto)
    {
      re: /\[([^\]]+)\]\((https?:\/\/[^\s)]+|mailto:[^\s)]+)\)/,
      node: (m, key) => (
        <a
          key={key}
          href={m[2]}
          target={m[2].startsWith("http") ? "_blank" : undefined}
          rel="noreferrer"
          className="text-[15px] font-medium"
          style={{ color: "var(--cd-blue-500)" }}
        >
          {m[1]}
        </a>
      ),
    },
    // **bold**
    {
      re: /\*\*([^*]+)\*\*/,
      node: (m, key) => <strong key={key} className="not-italic font-medium">{m[1]}</strong>,
    },
    // _italic_
    {
      re: /_([^_]+)_/,
      node: (m, key) => <em key={key}>{m[1]}</em>,
    },
    // `code`
    {
      re: /`([^`]+)`/,
      node: (m, key) => (
        <span key={key} className="font-mono text-[13px] bg-[#F5F5F0] px-1 py-0.5 rounded-sm">
          {m[1]}
        </span>
      ),
    },
  ];

  let keyCounter = 0;
  for (const pass of passes) {
    const next = [];
    for (const seg of segs) {
      if (typeof seg !== "string") { next.push(seg); continue; }
      let rest = seg;
      while (true) {
        const m = rest.match(pass.re);
        if (!m) { next.push(rest); break; }
        if (m.index > 0) next.push(rest.slice(0, m.index));
        next.push(pass.node(m, keyCounter++));
        rest = rest.slice(m.index + m[0].length);
      }
    }
    segs = next;
  }

  // Preserve single newlines within a paragraph as <br/>
  const finalOut = [];
  for (const seg of segs) {
    if (typeof seg !== "string") { finalOut.push(seg); continue; }
    const lines = seg.split("\n");
    lines.forEach((line, i) => {
      if (i > 0) finalOut.push(<br key={`br-${keyCounter++}`} />);
      finalOut.push(line);
    });
  }
  return finalOut;
}
